import { useEffect, useRef, useState } from "react";

const designedMediaWidth = 393;
const designedMediaHeight = 316;
const thumbnailRailHeight = 92;

export interface CompactProofHeroGeometry {
  contentWidth: number;
  headerHeight: number;
  heroAspectRatio: number;
  overlap: number;
  railPossible: boolean;
  ratingFoldReserve?: number;
}

export function shouldCompactProofHero(
  enabled: boolean,
  viewport: { width: number; height: number },
  geometry: CompactProofHeroGeometry,
) {
  if (!enabled || viewport.width <= 0 || viewport.height <= 0) return false;

  const width = Math.min(viewport.width, geometry.contentWidth);
  const aspectRatioHeight = width / geometry.heroAspectRatio;
  const designedHeight = (width * designedMediaHeight) / designedMediaWidth;
  const requiredMediaHeight = geometry.railPossible
    ? designedHeight + thumbnailRailHeight + geometry.overlap
    : 0;
  const naturalHeroHeight = Math.max(aspectRatioHeight, requiredMediaHeight);
  const neededHeight =
    geometry.headerHeight +
    naturalHeroHeight -
    geometry.overlap +
    (geometry.ratingFoldReserve ?? 184);

  return viewport.height < neededHeight;
}

/**
 * Decide from the initial visible viewport and keep that choice stable while
 * mobile browser chrome or the software keyboard changes height. Width changes
 * (rotation and browser zoom) intentionally recompute the layout.
 */
export function useCompactProofHero(
  enabled: boolean,
  geometry: CompactProofHeroGeometry,
) {
  const initialWidth = typeof window === "undefined" ? 0 : window.innerWidth;
  const widthRef = useRef(initialWidth);
  const [compact, setCompact] = useState(() =>
    typeof window === "undefined"
      ? false
      : shouldCompactProofHero(
          enabled,
          { width: window.innerWidth, height: window.innerHeight },
          geometry,
        ),
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const recompute = () => {
      if (window.innerWidth === widthRef.current) return;
      widthRef.current = window.innerWidth;
      setCompact(
        shouldCompactProofHero(
          enabled,
          { width: window.innerWidth, height: window.innerHeight },
          geometry,
        ),
      );
    };

    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [enabled, geometry]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setCompact(
      shouldCompactProofHero(
        enabled,
        { width: window.innerWidth, height: window.innerHeight },
        geometry,
      ),
    );
  }, [enabled, geometry]);

  return compact;
}
